import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Share2, Check, Link, X } from 'lucide-react'
import { SpeedTestResult } from './SpeedTest'

interface ShareResultsProps {
  results: SpeedTestResult
}

const ShareResults: React.FC<ShareResultsProps> = ({ results }) => {
  const { t } = useTranslation()
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)

  const generateShareText = () => {
    return `I just tested my internet speed on SpeedTestry!\n\n` +
           `Download: ${results.downloadSpeed.toFixed(1)} Mbps\n` +
           `Upload: ${results.uploadSpeed.toFixed(1)} Mbps\n` +
           `Ping: ${Math.round(results.ping)} ms\n\n` +
           `Test your speed at speedtestry.com`
  }

  const generateShareUrl = () => {
    const params = new URLSearchParams({
      d: results.downloadSpeed.toFixed(1),
      u: results.uploadSpeed.toFixed(1),
      p: Math.round(results.ping).toString(),
      t: results.timestamp.toString()
    })
    return `${window.location.origin}?${params.toString()}`
  }

  const shareToTwitter = () => {
    const text = generateShareText()
    const url = generateShareUrl()
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    window.open(twitterUrl, '_blank', 'width=550,height=420')
    setShowShareMenu(false)
  }

  const shareToFacebook = () => {
    const url = generateShareUrl()
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    window.open(facebookUrl, '_blank', 'width=550,height=420')
    setShowShareMenu(false)
  }

  const copyLink = async () => {
    try {
      const url = generateShareUrl()
      await navigator.clipboard.writeText(url)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }

  const shareNative = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({
          title: 'SpeedTestry Results',
          text: generateShareText(),
          url: generateShareUrl()
        })
        setShowShareMenu(false)
      } catch (error) {
        console.error('Native share failed:', error)
      }
    }
  }

  const menuVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: -10
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.1, 0.25, 1]
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      y: -10,
      transition: {
        duration: 0.15
      }
    }
  }

  return (
    <div className="relative">
      <motion.button
        onClick={() => setShowShareMenu(!showShareMenu)}
        className="apple-btn"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Share2 className="w-5 h-5 mr-2" />
        {t('shareResults')}
      </motion.button>

      <AnimatePresence>
        {showShareMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowShareMenu(false)}
            />
            
            {/* Menu */}
            <motion.div 
              className="absolute bottom-full mb-2 right-0 bg-white rounded-apple-md shadow-apple-lg border border-apple-gray-100 p-2 min-w-[220px] z-50"
              variants={menuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Close button */}
              <div className="flex justify-between items-center px-3 py-2 mb-1">
                <span className="text-sm font-medium text-apple-gray-900">Share</span>
                <button 
                  onClick={() => setShowShareMenu(false)}
                  className="p-1 hover:bg-apple-gray-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-apple-gray-500" />
                </button>
              </div>

              <div className="space-y-1">
                {/* Native Share (if supported) */}
                {typeof navigator !== 'undefined' && 'share' in navigator && (
                  <motion.button
                    onClick={shareNative}
                    className="w-full flex items-center space-x-3 px-3 py-2.5 text-apple-gray-700 hover:bg-apple-gray-50 rounded-apple transition-colors"
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-apple-gray-100 rounded-full">
                      <Share2 className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium">Share...</span>
                  </motion.button>
                )}

                {/* Twitter */}
                <motion.button
                  onClick={shareToTwitter}
                  className="w-full flex items-center space-x-3 px-3 py-2.5 text-apple-gray-700 hover:bg-apple-gray-50 rounded-apple transition-colors"
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-[#1DA1F2]/10 rounded-full">
                    <svg className="w-4 h-4 text-[#1DA1F2]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">{t('shareOn', { platform: 'X' })}</span>
                </motion.button>

                {/* Facebook */}
                <motion.button
                  onClick={shareToFacebook}
                  className="w-full flex items-center space-x-3 px-3 py-2.5 text-apple-gray-700 hover:bg-apple-gray-50 rounded-apple transition-colors"
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-[#1877F2]/10 rounded-full">
                    <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">{t('shareOn', { platform: 'Facebook' })}</span>
                </motion.button>

                {/* Copy Link */}
                <motion.button
                  onClick={copyLink}
                  className="w-full flex items-center space-x-3 px-3 py-2.5 text-apple-gray-700 hover:bg-apple-gray-50 rounded-apple transition-colors"
                  whileTap={{ scale: 0.98 }}
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${linkCopied ? 'bg-apple-green/10' : 'bg-apple-gray-100'}`}>
                    {linkCopied ? (
                      <Check className="w-4 h-4 text-apple-green" />
                    ) : (
                      <Link className="w-4 h-4 text-apple-gray-600" />
                    )}
                  </div>
                  <span className="text-sm font-medium">
                    {linkCopied ? t('linkCopied') : t('copyLink')}
                  </span>
                </motion.button>
              </div>

              {/* Preview */}
              <div className="mt-3 pt-3 border-t border-apple-gray-100">
                <div className="px-3">
                  <div className="text-xs text-apple-gray-500 mb-2">Preview</div>
                  <div className="text-xs text-apple-gray-600 bg-apple-gray-50 p-3 rounded-apple leading-relaxed">
                    <div className="font-medium text-apple-gray-900 mb-1">SpeedTestry Results</div>
                    <div className="space-y-0.5 font-mono">
                      <div>↓ {results.downloadSpeed.toFixed(1)} Mbps</div>
                      <div>↑ {results.uploadSpeed.toFixed(1)} Mbps</div>
                      <div>⚡ {Math.round(results.ping)} ms</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ShareResults
